import { all, call, put, takeLatest, select } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import {
  getProjectsListSagaAction,
  getProjectDetailsSagaAction,
  projectsSagaActionTypes,
  getIntegrationDetailSagaAction,
  getScreenshotsSagaAction,
} from "./saga-actions";
import { projectsSliceAction } from "../../slices/projects";
import {
  ProjectsAPIResponse,
  ProjectAPIResponse,
  CreateTimelineEntryRequest,
  CreateTimelineEntryResponse,
  UpdateTimelineEntryRequest,
  UpdateTimelineEntryResponse,
  IntegrationDetail,
  UpdateEnvironmentDetailsRequest,
  UpdateEnvironmentDetailsResponse,
  UpdateProjectDescriptionRequest,
  UpdateProjectDescriptionResponse,
  UspFeaturesAPIResponse,
  IntegrationValidationSummary,
  GetScreenshotsAPIResponse,
  ScreenshotFileDTO,
  ScreenshotFile,
  UpsertScreenshotsRequest,
  UpsertScreenshotsResponse,
  DeleteScreenshotResponse,
} from "../../slices/projects/Types";
import { getValidationDocumentationSagaAction } from "../validation-documentation/saga-actions";
import {
  getFileViewUrlAPI,
  getFileViewUrlAPIResponseType,
} from "../onboarding-form";
import { getDocumentationIds } from "../../../utils/getDocumentationIds";

const normalizeIntegrationSummaries = (
  response: ProjectAPIResponse
): Record<number, IntegrationValidationSummary> => {
  const summaries: Record<number, IntegrationValidationSummary> = {};

  const register = (entry?: IntegrationValidationSummary | null) => {
    if (!entry || entry.integration_id == null) return;
    summaries[Number(entry.integration_id)] = entry;
  };

  register(response.summary);

  response.data?.technologies?.forEach((technology) => {
    technology.integrations?.forEach((integration) => {
      register(integration.summary);
    });
  });

  return summaries;
};

// ----------------------------------------
// API
const getProjectsListAPI = async (partnerID: number) => {
  const response = await axiosRestInstance.get<
    AxiosResponse<ProjectsAPIResponse>
  >(`/project/partner/${partnerID}`);
  return response.data;
};

const getProjectDetailsAPI = async (projectName: string, partnerId: number) => {
  const encodedProjectName = encodeURIComponent(projectName);
  const response = await axiosRestInstance.get<
    AxiosResponse<ProjectAPIResponse>
  >(`/project/name/${encodedProjectName}`, {
    params: { partner_id: partnerId },
  });
  return response.data;
};

export type IntegrationDetailAPIResponse = {
  success: boolean;
  data: IntegrationDetail;
};

const getIntegrationDetailAPI = async (integrationId: number) => {
  const response = await axiosRestInstance.get<
    AxiosResponse<IntegrationDetailAPIResponse>
  >(`/project/integration/${integrationId}`);
  return response.data;
};

const createTimelineEntryAPI = async (
  integrationId: number,
  payload: CreateTimelineEntryRequest,
) => {
  const response = await axiosRestInstance.post<
    AxiosResponse<CreateTimelineEntryResponse>
  >(`/timeline/create/${integrationId}`, payload);
  return response.data;
};

const updateTimelineEntryAPI = async (
  timelineEntryId: number,
  payload: UpdateTimelineEntryRequest,
) => {
  const response = await axiosRestInstance.put<
    AxiosResponse<UpdateTimelineEntryResponse>
  >(`/timeline/Update/${timelineEntryId}`, payload);
  return response.data;
};

const updateEnvironmentDetailsAPI = async (
  integrationId: number,
  payload: UpdateEnvironmentDetailsRequest,
  file?: File | null,
) => {
  // Use FormData for file upload
  const formData = new FormData();

  // Append all payload fields
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });

  // Append file if provided
  if (file) {
    formData.append("sequence_flow_diagram", file);
  }

  const response = await axiosRestInstance.put<
    AxiosResponse<UpdateEnvironmentDetailsResponse>
  >(`/project/integration-validation/${integrationId}/envUpdate`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

const updateProjectDescriptionAPI = async (
  technologyId: number,
  payload: UpdateProjectDescriptionRequest,
) => {
  const response = await axiosRestInstance.put<
    AxiosResponse<UpdateProjectDescriptionResponse>
  >(`/project-technology/${technologyId}/description`, payload);
  return response.data;
};

const getUspDetailsAPI = async (
  technologyId: number,
  integrationTypeId: number,
  integrationId: number,
  documentationId: number,
) => {
  const response = await axiosRestInstance.get<
    AxiosResponse<UspFeaturesAPIResponse>
  >(`/project/feature/USP`, {
    params: {
      technology_id: technologyId,
      integration_type_id: integrationTypeId,
      integration_id: integrationId,
      documentation_id: documentationId,
    },
  });
  return response.data;
};

const getScreenshotsAPI = async (
  screenshotType: string,
  integrationTypeId: number,
) => {
  const response = await axiosRestInstance.get<GetScreenshotsAPIResponse>(
    `/getScreenshots/${encodeURIComponent(screenshotType)}/${integrationTypeId}`,
  );
  return response.data;
};

const upsertScreenshotsAPI = async (payload: UpsertScreenshotsRequest) => {
  const formData = new FormData();
  formData.append("uploaded_by", String(payload.uploadedBy));
  formData.append("integration_type_id", String(payload.integrationTypeId));
  formData.append("template", payload.template);
  formData.append("selected_media_ids", JSON.stringify(payload.selectedMediaIds));
  formData.append("selected_indexes", JSON.stringify(payload.selectedIndexes));
  payload.files.forEach((file) => formData.append("file_path", file));

  const response = await axiosRestInstance.post<UpsertScreenshotsResponse>(
    `/upsertScreenshot`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};

const deleteScreenshotAPI = async (mediaId: number) => {
  const response = await axiosRestInstance.delete<DeleteScreenshotResponse>(
    `/deleteScreenshots/${mediaId}`,
  );
  return response.data;
};

// ----------------------------------------
// Sagas

function* getProjectsListSaga(action: { type: string; partnerID: number }) {
  try {
    yield put(projectsSliceAction.setProjectsListLoading(true));
    const response: ProjectsAPIResponse = yield call(
      getProjectsListAPI,
      action.partnerID,
    );
    yield put(projectsSliceAction.setProjectsList(response.data || []));
  } catch (e) {
    console.log(e);
    yield put(projectsSliceAction.setProjectsList([]));
  } finally {
    yield put(projectsSliceAction.setProjectsListLoading(false));
  }
}

function* getProjectDetailsSaga(action: {
  type: string;
  projectName: string;
  partnerId: number;
}) {
  try {
    yield put(projectsSliceAction.setProjectDetailsLoading(true));
    const response: ProjectAPIResponse = yield call(
      getProjectDetailsAPI,
      action.projectName,
      action.partnerId,
    );
    yield put(projectsSliceAction.setProjectDetails(response.data || null));
    yield put(
      projectsSliceAction.setIntegrationSummaries(
        normalizeIntegrationSummaries(response)
      )
    );
  } catch (e) {
    console.log(e);
    yield put(projectsSliceAction.setProjectDetails(null));
    yield put(projectsSliceAction.setIntegrationSummaries({}));
  } finally {
    yield put(projectsSliceAction.setProjectDetailsLoading(false));
  }
}

function* refetchProjectDetails(): Generator<any, void, any> {
  const state: any = yield select();
  const projectName = state.projects?.projectDetails?.project_name;
  const partnerId =
    state.partner_details?.partnerDetails?.ID ??
    state.projects?.projectDetails?.partner_id;
  if (projectName && partnerId != null) {
    yield put(getProjectDetailsSagaAction(projectName, Number(partnerId)));
  }
}

function* getIntegrationDetailSaga(action: {
  type: string;
  integrationId: number;
}) {
  try {
    yield put(projectsSliceAction.setIntegrationDetailLoading(true));
    const response: IntegrationDetailAPIResponse = yield call(
      getIntegrationDetailAPI,
      action.integrationId,
    );
    yield put(projectsSliceAction.setIntegrationDetail(response.data || null));
  } catch (e) {
    console.error("Error fetching integration detail:", e);
    yield put(projectsSliceAction.setIntegrationDetail(null));
  } finally {
    yield put(projectsSliceAction.setIntegrationDetailLoading(false));
  }
}

function* createTimelineEntrySaga(action: {
  type: string;
  integrationId: number;
  payload: CreateTimelineEntryRequest;
}): Generator<any, void, any> {
  try {
    const response: CreateTimelineEntryResponse = yield call(
      createTimelineEntryAPI,
      action.integrationId,
      action.payload,
    );

    // Refetch project details to get updated timeline
    yield call(refetchProjectDetails);
  } catch (e) {
    console.error("Error creating timeline entry:", e);
    // Optionally show error notification to user
  }
}

function* updateTimelineEntrySaga(action: {
  type: string;
  timelineEntryId: number;
  payload: UpdateTimelineEntryRequest;
}): Generator<any, void, any> {
  try {
    yield put(projectsSliceAction.setTimelineUpdateLoading(true));
    const response: UpdateTimelineEntryResponse = yield call(
      updateTimelineEntryAPI,
      action.timelineEntryId,
      action.payload,
    );

    // Refetch project details to get updated timeline
    yield call(refetchProjectDetails);
  } catch (e) {
    console.error("Error updating timeline entry:", e);
    // Optionally show error notification to user
  } finally {
    yield put(projectsSliceAction.setTimelineUpdateLoading(false));
  }
}

function* updateEnvironmentDetailsSaga(action: {
  type: string;
  technology: string;
  integrationType: string;
  integrationId: number;
  payload: UpdateEnvironmentDetailsRequest;
  file?: File | null;
}): Generator<any, void, any> {
  try {
    yield put(projectsSliceAction.setIntegrationUpdateLoading(true));
    const response: UpdateEnvironmentDetailsResponse = yield call(
      updateEnvironmentDetailsAPI,
      action.integrationId,
      action.payload,
      action.file,
    );

    // Set success status
    yield put(projectsSliceAction.setIntegrationUpdateSuccess(true));

    const state: any = yield select();
    const documentationIds = getDocumentationIds(
      state.projects?.projectDetails,
      action.integrationId,
      state.projects?.integrationDetail,
    );

    if (documentationIds) {
      yield put(
        getValidationDocumentationSagaAction(
          documentationIds.technologyId,
          documentationIds.integrationTypeId,
          action.integrationId,
        ),
      );
    }
  } catch (e) {
    console.error("Error updating integration:", e);
    yield put(projectsSliceAction.setIntegrationUpdateSuccess(false));
  } finally {
    yield put(projectsSliceAction.setIntegrationUpdateLoading(false));
  }
}

// ----------------------------------------
// Root Saga

function* updateProjectDescriptionSaga(action: {
  type: string;
  technologyId: number;
  payload: UpdateProjectDescriptionRequest;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}): Generator<any, void, any> {
  try {
    yield put(projectsSliceAction.setProjectDescriptionUpdateLoading(true));
    const response: UpdateProjectDescriptionResponse = yield call(
      updateProjectDescriptionAPI,
      action.technologyId,
      action.payload,
    );

    if (!response.success) {
      action.onError?.(
        response.message || "Failed to update project description."
      );
      return;
    }

    yield call(refetchProjectDetails);

    action.onSuccess?.();
  } catch (e: any) {
    console.error("Error updating project description:", e);
    action.onError?.(
      e?.response?.data?.message || "Failed to update project description."
    );
  } finally {
    yield put(projectsSliceAction.setProjectDescriptionUpdateLoading(false));
  }
}

function* getUspDetailsSaga(action: {
  type: string;
  technologyId: number;
  integrationTypeId: number;
  integrationId: number;
  documentationId: number,
}): Generator<any, void, any> {
  try {
    yield put(projectsSliceAction.setUspDetailsLoading(true));
    const response: UspFeaturesAPIResponse = yield call(
      getUspDetailsAPI,
      action.technologyId,
      action.integrationTypeId,
      action.integrationId,
      action.documentationId,
    );
    yield put(projectsSliceAction.setUspDetails(response.data || []));
  } catch (e) {
    console.error("Error fetching USP details:", e);
    yield put(projectsSliceAction.setUspDetails([]));
  } finally {
    yield put(projectsSliceAction.setUspDetailsLoading(false));
  }
}

function* resolveScreenshotViewUrl(
  file: ScreenshotFileDTO,
): Generator<any, ScreenshotFile, any> {
  if (!file.file_path) return { ...file, view_url: null };
  try {
    const response: getFileViewUrlAPIResponseType = yield call(
      getFileViewUrlAPI,
      file.file_path,
      "view",
    );
    return { ...file, view_url: response?.data?.url || response?.url || null };
  } catch (e) {
    console.error(`Error resolving screenshot URL for ${file.file_name}:`, e);
    return { ...file, view_url: null };
  }
}

const sortByDisplayOrder = (files: ScreenshotFile[]) =>
  [...files].sort(
    (a, b) =>
      (a.display_order ?? Number.MAX_SAFE_INTEGER) -
      (b.display_order ?? Number.MAX_SAFE_INTEGER),
  );

function* getScreenshotsSaga(action: {
  type: string;
  integrationTypeId: number;
  screenshotType: string;
}): Generator<any, void, any> {
  try {
    const current = yield select((state) => state.projects.screenshots);
    if (current && current.integrationTypeId !== action.integrationTypeId) {
      yield put(projectsSliceAction.setScreenshots(null));
    }
    yield put(projectsSliceAction.setScreenshotsLoading(true));

    const response: GetScreenshotsAPIResponse = yield call(
      getScreenshotsAPI,
      action.screenshotType,
      action.integrationTypeId,
    );

    const selectedDTOs = response?.selected_screenshots || [];
    const availableDTOs = response?.available_screenshots || [];
    const [selected, available]: [ScreenshotFile[], ScreenshotFile[]] =
      yield all([
        all(selectedDTOs.map((file) => call(resolveScreenshotViewUrl, file))),
        all(availableDTOs.map((file) => call(resolveScreenshotViewUrl, file))),
      ]);

    yield put(
      projectsSliceAction.setScreenshots({
        integrationTypeId: action.integrationTypeId,
        template: response?.template || "three_mobile",
        uploadedCount:
          response?.uploaded_count ?? selected.length + available.length,
        maxCount: response?.max_count ?? 10,
        maxSelected: response?.max_selected ?? selected.length,
        selected: sortByDisplayOrder(selected),
        available,
      }),
    );
  } catch (e) {
    console.error("Error fetching screenshots:", e);
    yield put(projectsSliceAction.setScreenshots(null));
  } finally {
    yield put(projectsSliceAction.setScreenshotsLoading(false));
  }
}

function* upsertScreenshotsSaga(action: {
  type: string;
  payload: UpsertScreenshotsRequest;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}): Generator<any, void, any> {
  try {
    yield put(projectsSliceAction.setScreenshotsSaving(true));
    const response: UpsertScreenshotsResponse = yield call(
      upsertScreenshotsAPI,
      action.payload,
    );

    if (response && response.success === false) {
      action.onError?.(response.message || "Failed to save screenshots.");
      return;
    }

    yield put(getScreenshotsSagaAction(action.payload.integrationTypeId));
    action.onSuccess?.();
  } catch (e: any) {
    console.error("Error saving screenshots:", e);
    action.onError?.(
      e?.response?.data?.message || "Failed to save screenshots.",
    );
  } finally {
    yield put(projectsSliceAction.setScreenshotsSaving(false));
  }
}

function* deleteScreenshotSaga(action: {
  type: string;
  mediaId: number;
  integrationTypeId: number;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}): Generator<any, void, any> {
  try {
    const response: DeleteScreenshotResponse = yield call(
      deleteScreenshotAPI,
      action.mediaId,
    );

    if (response && response.success === false) {
      action.onError?.(response.message || "Failed to delete screenshot.");
      return;
    }

    action.onSuccess?.();
    yield put(getScreenshotsSagaAction(action.integrationTypeId));
  } catch (e: any) {
    console.error("Error deleting screenshot:", e);
    action.onError?.(
      e?.response?.data?.message || "Failed to delete screenshot.",
    );
  }
}

function* projectsSaga() {
  yield takeLatest(
    projectsSagaActionTypes.callGetProjectsListSaga,
    getProjectsListSaga,
  );
  yield takeLatest(
    projectsSagaActionTypes.callGetProjectDetailsSaga,
    getProjectDetailsSaga,
  );
  yield takeLatest(
    projectsSagaActionTypes.callGetIntegrationDetailSaga,
    getIntegrationDetailSaga,
  );
  yield takeLatest(
    projectsSagaActionTypes.callCreateTimelineEntrySaga,
    createTimelineEntrySaga,
  );
  yield takeLatest(
    projectsSagaActionTypes.callUpdateTimelineEntrySaga,
    updateTimelineEntrySaga,
  );
  yield takeLatest(
    projectsSagaActionTypes.callUpdateEnvironmentDetailsSaga,
    updateEnvironmentDetailsSaga,
  );
  yield takeLatest(
    projectsSagaActionTypes.callUpdateProjectDescriptionSaga,
    updateProjectDescriptionSaga,
  );
  yield takeLatest(
    projectsSagaActionTypes.callGetUspDetailsSaga,
    getUspDetailsSaga,
  );
  yield takeLatest(
    projectsSagaActionTypes.callGetScreenshotsSaga,
    getScreenshotsSaga,
  );
  yield takeLatest(
    projectsSagaActionTypes.callUpsertScreenshotsSaga,
    upsertScreenshotsSaga,
  );
  yield takeLatest(
    projectsSagaActionTypes.callDeleteScreenshotSaga,
    deleteScreenshotSaga,
  );
}

export default projectsSaga;
